import { useMemo, useState } from 'react';
import { ImagePlus, Sparkles, Upload, X, Expand, Download, ChevronLeft, ChevronRight, Image as ImageIcon, Wand2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useImageStudio } from '../hooks/useImageStudio';
import type { ImageGeneration, ImageGenerationInputUpload } from '../types';

const ASPECT_RATIOS = ['1:1', '4:5', '3:4', '16:9', '9:16'] as const;
const SIZE_OPTIONS = ['1024', '1536', '2048'] as const;

export function ImageStudioPage() {
  const { items, loading, error, uploadInputs, queueGeneration } = useImageStudio();
  const [prompt, setPrompt] = useState('');
  const [mode, setMode] = useState<'image_only' | 'text_plus_image'>('text_plus_image');
  const [aspectRatio, setAspectRatio] = useState<string>('1:1');
  const [size, setSize] = useState<string>('1024');
  const [count, setCount] = useState<number>(1);
  const [uploads, setUploads] = useState<ImageGenerationInputUpload[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [lightbox, setLightbox] = useState<{ images: Array<{ downloadUrl?: string; filename?: string }>; index: number } | null>(null);

  const pendingItems = useMemo(() => items.filter((item) => item.status === 'queued' || item.status === 'running'), [items]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    const uploaded = await uploadInputs(files);
    setUploads((prev) => [...prev, ...uploaded]);
    event.target.value = '';
  };

  const handleSubmit = async () => {
    if (!prompt.trim()) return;
    setSubmitting(true);
    try {
      await queueGeneration({ prompt, mode, aspectRatio, size, count, inputImages: uploads });
      setPrompt('');
      setCount(1);
      setUploads([]);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900"><ImagePlus className="h-7 w-7 text-brand-green" /> Image Studio</h1>
          <p className="mt-1 text-sm text-gray-500">Gemini 3.1 Flash Image Preview tab for prompting, remixing, and browsing generated sets.</p>
        </div>
        {pendingItems.length > 0 && <div className="rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700">{pendingItems.length} generation{pendingItems.length !== 1 ? 's' : ''} in progress</div>}
      </div>

      <div className="grid gap-6 xl:grid-cols-[380px,1fr]">
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm space-y-4">
          <div>
            <label className="label mb-2">Prompt</label>
            <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={6} className="input min-h-[140px] resize-y" placeholder="Describe what you want to create or how you want to transform your reference images..." />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label mb-2">Mode</label>
              <select className="input" value={mode} onChange={(e) => setMode(e.target.value as 'image_only' | 'text_plus_image')}>
                <option value="text_plus_image">Text + image</option>
                <option value="image_only">Image only remix</option>
              </select>
            </div>
            <div>
              <label className="label mb-2">Batch count</label>
              <select className="input" value={count} onChange={(e) => setCount(parseInt(e.target.value, 10))}>
                {[1,2,3,4].map((value) => <option key={value} value={value}>{value}</option>)}
              </select>
            </div>
            <div>
              <label className="label mb-2">Aspect ratio</label>
              <select className="input" value={aspectRatio} onChange={(e) => setAspectRatio(e.target.value)}>
                {ASPECT_RATIOS.map((value) => <option key={value} value={value}>{value}</option>)}
              </select>
            </div>
            <div>
              <label className="label mb-2">Resolution</label>
              <select className="input" value={size} onChange={(e) => setSize(e.target.value)}>
                {SIZE_OPTIONS.map((value) => <option key={value} value={value}>{value}px</option>)}
              </select>
            </div>
          </div>

          <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-gray-900">Reference images</p>
                <p className="text-xs text-gray-500">Upload any number of inputs to guide the generation.</p>
              </div>
              <label className="btn-secondary cursor-pointer gap-2">
                <Upload className="h-4 w-4" />
                Add Images
                <input type="file" accept="image/*" multiple className="hidden" onChange={handleFileChange} />
              </label>
            </div>
            {uploads.length > 0 ? (
              <div className="mt-4 grid grid-cols-3 gap-3">
                {uploads.map((upload, index) => (
                  <div key={`${upload.s3Key}-${index}`} className="group relative overflow-hidden rounded-xl border border-gray-200 bg-white">
                    <img src={upload.previewUrl} alt={upload.filename} className="aspect-square w-full object-cover" />
                    <button onClick={() => setUploads((prev) => prev.filter((item) => item.s3Key !== upload.s3Key))} className="absolute right-2 top-2 rounded-full bg-black/60 p-1 text-white opacity-0 transition group-hover:opacity-100">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-4 flex items-center justify-center rounded-xl bg-white p-8 text-center text-sm text-gray-400"><ImageIcon className="mr-2 h-4 w-4" /> No reference images uploaded yet</div>
            )}
          </div>

          <button onClick={handleSubmit} disabled={submitting || !prompt.trim() || (mode === 'image_only' && uploads.length === 0)} className="btn-primary w-full gap-2">
            <Sparkles className="h-4 w-4" />
            {submitting ? 'Queueing generation...' : 'Generate images'}
          </button>
        </div>

        <div className="space-y-4">
          {error && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
          {loading ? <div className="rounded-2xl border border-gray-200 bg-white p-10 text-center text-sm text-gray-500">Loading generations...</div> : null}
          {!loading && items.length === 0 ? <div className="rounded-2xl border border-gray-200 bg-white p-10 text-center text-sm text-gray-500">No generations yet. Create your first prompt on the left.</div> : null}
          {items.map((item) => <ConversationCard key={item.imageGenerationId} item={item} onOpenLightbox={(images, index) => setLightbox({ images, index })} />)}
        </div>
      </div>

      {lightbox && <Lightbox images={lightbox.images} index={lightbox.index} onClose={() => setLightbox(null)} onChange={(index) => setLightbox((prev) => prev ? { ...prev, index } : prev)} />}
    </div>
  );
}

function ConversationCard({ item, onOpenLightbox }: { item: ImageGeneration; onOpenLightbox: (images: Array<{ downloadUrl?: string; filename?: string }>, index: number) => void }) {
  const generatedImages = item.outputImageUrls || [];
  return (
    <div className="space-y-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-green/10 text-brand-green"><Wand2 className="h-5 w-5" /></div>
        <div className="flex-1">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-gray-900">Prompt</p>
              <p className="mt-1 text-sm text-gray-700 whitespace-pre-wrap">{item.prompt}</p>
            </div>
            <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${item.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : item.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{item.status}</span>
          </div>
          <div className="mt-3 flex flex-wrap gap-2 text-xs text-gray-500">
            <span>{item.mode === 'image_only' ? 'Image only' : 'Text + image'}</span>
            <span>•</span>
            <span>{item.aspectRatio}</span>
            <span>•</span>
            <span>{item.size}px</span>
            <span>•</span>
            <span>{item.count} requested</span>
            <span>•</span>
            <span>{formatDistanceToNow(new Date(item.createdAt), { addSuffix: true })}</span>
          </div>
          {(item.inputImageUrls?.length || 0) > 0 && (
            <div className="mt-4 grid grid-cols-4 gap-2">
              {item.inputImageUrls?.map((img, index) => <img key={`${img.s3Key}-${index}`} src={img.downloadUrl} alt={img.filename || `input-${index + 1}`} className="aspect-square rounded-xl object-cover" />)}
            </div>
          )}
        </div>
      </div>

      <div className="ml-13 rounded-2xl bg-gray-50 p-4">
        {item.status === 'failed' ? <p className="text-sm text-red-700">{item.error || 'Generation failed.'}</p> : null}
        {(item.status === 'queued' || item.status === 'running') ? <p className="text-sm text-gray-500">Generating with Gemini 3.1 Flash Image Preview…</p> : null}
        {generatedImages.length > 0 && (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {generatedImages.map((image, index) => (
              <div key={`${image.s3Key}-${index}`} className="group relative overflow-hidden rounded-2xl border border-gray-200 bg-white">
                <button className="block w-full" onClick={() => onOpenLightbox(generatedImages, index)}>
                  <img src={image.downloadUrl} alt={image.filename || `generated-${index + 1}`} className="aspect-square w-full object-cover transition duration-200 group-hover:scale-[1.02]" />
                </button>
                <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-black/70 to-transparent px-3 py-3 text-white opacity-0 transition group-hover:opacity-100">
                  <button onClick={() => onOpenLightbox(generatedImages, index)} className="rounded-lg bg-white/20 p-2"><Expand className="h-4 w-4" /></button>
                  <a href={image.downloadUrl} download={image.filename || `generated-${index + 1}.png`} className="rounded-lg bg-white/20 p-2"><Download className="h-4 w-4" /></a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Lightbox({ images, index, onClose, onChange }: { images: Array<{ downloadUrl?: string; filename?: string }>; index: number; onClose: () => void; onChange: (index: number) => void }) {
  const current = images[index];
  const prev = () => onChange(index === 0 ? images.length - 1 : index - 1);
  const next = () => onChange(index === images.length - 1 ? 0 : index + 1);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4" onClick={onClose}>
      <div className="relative w-full max-w-6xl" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute right-0 top-0 rounded-full bg-white/10 p-3 text-white"><X className="h-5 w-5" /></button>
        <div className="flex items-center justify-between pb-4 pr-14 text-white">
          <div className="text-sm">Image {index + 1} of {images.length}</div>
          <a href={current?.downloadUrl} download={current?.filename || `generated-${index + 1}.png`} className="btn-secondary gap-2 border-white/20 bg-white/10 text-white hover:bg-white/20"><Download className="h-4 w-4" />Download</a>
        </div>
        <div className="relative overflow-hidden rounded-2xl bg-black">
          <img src={current?.downloadUrl} alt={current?.filename || `generated-${index + 1}`} className="mx-auto max-h-[78vh] w-auto max-w-full object-contain" />
          {images.length > 1 && (
            <>
              <button onClick={prev} className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white"><ChevronLeft className="h-5 w-5" /></button>
              <button onClick={next} className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white"><ChevronRight className="h-5 w-5" /></button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
